import _ from "lodash";
import React from "react";

import classes from "./PostList.scss";
import {
  PLACEHOLDER_RECT_IMAGE,
  PLACEHOLDER_RECT_IMAGE_COUNT,
} from "Constants";
import I18nHOC from "UIComponents/I18nHOC";
import ResourceImagePlaceHolder from "UIComponents/ResourceImagePlaceHolder";
import { EmptyState, IconButton } from "@toddle-edu/ds-web";
import { EvidenceIllustration } from "@toddle-edu/ds-theme";
import {
  ChevronRightOutlined,
  LeOutlined,
  TagOutlined,
  UnitPlanOutlined,
  SaOutlined,
  FaOutlined,
} from "@toddle-edu/ds-icons";
import {
  getIsNewGoalTaggingEnabled,
  getTaggedAssessment,
  isAssessmentTagged,
  isUnitPlanTagged,
  getTaggedUnitNames,
} from "Post/utils";
import AssessmentIcon from "UIComponents/AssessmentIcon";

const styles = {
  newImagePlaceholder: {
    borderRadius: "8px 0px 0px 8px",
    height: "72px",
    width: "72px",
    flex: "inherit",
  },
  imagePlaceholder: {
    height: "64px",
    width: "80px",
    flex: "inherit",
  },
  listStyleWithAlert: {
    padding: "16px 0px",
  },
  listStyleWithoutAlert: {
    paddingBottom: "16px ",
  },
  unitNameStyle: {
    width: "fit-content",
    maxWidth: "30ch",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
};

const emptyStateSubtitleStyle = {
  lineHeight: "24px",
};

const PostList = props => {
  const renderPosts = () => {
    const { onItemClicked, t, feedList, isUserTypeStudent } = props;

    const getGoalsAndStudentCount = post => {
      return {
        goalsCount: _.reduce(
          _.get(post, "elementPYP", []),
          (acc, element) => {
            return acc + _.get(element, "value.length", 0);
          },
          0
        ),
        studentsCount: _.get(post, "students.totalCount", 0),
      };
    };

    const getPostImage = post => {
      let imageUrl = undefined;

      _.forEach(_.get(post, "attachments", []), attachment => {
        if (
          _.includes(
            ["IMAGE", "VIDEO", "WORKBOOK", "FILE"],
            _.get(attachment, "type")
          )
        ) {
          imageUrl = _.get(
            attachment,
            _.get(attachment, "type") === "IMAGE" ? "url" : "thumbUrl"
          );
        }
      });
      return imageUrl;
    };

    const getLeOrUnitName = post => {
      let leOrUnitName = null;
      const postTaggedEntity = _.get(post, "node.postTaggedEntity", []);
      const taggedAssesment = getTaggedAssessment(postTaggedEntity);
      const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();
      if (
        _.get(post, "node.resourceItem") ||
        (isNewGoalTaggingEnabled && isAssessmentTagged(postTaggedEntity))
      ) {
        const assessmentType = isNewGoalTaggingEnabled
          ? _.get(taggedAssesment, "entity.assessmentType.value") || "le"
          : _.get(post, "node.resourceItem.assessmentType.value") || "le";
        const taskTypeGroup = isNewGoalTaggingEnabled
          ? _.get(taggedAssesment, "entity.taskType.type")
          : _.get(post, "node.resourceItem.taskType.type");
        if (taskTypeGroup) {
          leOrUnitName = (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <AssessmentIcon
                assessmentType={assessmentType}
                size={"xxx-small"}
              />{" "}
              <span style={styles.unitNameStyle}>
                {" "}
                {isNewGoalTaggingEnabled
                  ? _.get(taggedAssesment, "entity.title.value")
                  : _.get(post, "node.resourceItem.title.value")}
              </span>
            </span>
          );
        }
        if (assessmentType === "le") {
          leOrUnitName = (
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <LeOutlined size={"xxx-small"} variant={"subtle"} />{" "}
              <span style={styles.unitNameStyle}>
                {" "}
                {isNewGoalTaggingEnabled
                  ? _.get(taggedAssesment, "entity.title.value")
                  : _.get(post, "node.resourceItem.title.value")}
              </span>
            </span>
          );
        } else if (assessmentType === "smt") {
          leOrUnitName = (
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <SaOutlined size={"xxx-small"} variant={"subtle"} />{" "}
              <span style={styles.unitNameStyle}>
                {" "}
                {isNewGoalTaggingEnabled
                  ? _.get(taggedAssesment, "entity.title.value")
                  : _.get(post, "node.resourceItem.title.value")}
              </span>
            </span>
          );
        } else if (assessmentType === "fmt") {
          leOrUnitName = (
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <FaOutlined size={"xxx-small"} variant={"subtle"} />{" "}
              <span style={styles.unitNameStyle}>
                {" "}
                {isNewGoalTaggingEnabled
                  ? _.get(taggedAssesment, "entity.title.value")
                  : _.get(post, "node.resourceItem.title.value")}
              </span>
            </span>
          );
        }
      } else if (
        _.get(post, "node.unitPlan") ||
        (isNewGoalTaggingEnabled &&
          isUnitPlanTagged(postTaggedEntity) &&
          !isAssessmentTagged(postTaggedEntity))
      ) {
        const unitPlansTagged = getTaggedUnitNames(postTaggedEntity);
        leOrUnitName = (
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UnitPlanOutlined size={"xxxx-small"} variant={"subtle"} />
            <span style={styles.unitNameStyle}>
              {" "}
              {isNewGoalTaggingEnabled
                ? _.size(unitPlansTagged) > 1
                  ? `${_.size(unitPlansTagged)} Units`
                  : _.get(_.first(postTaggedEntity), "entity.title.value")
                : _.get(post, "node.unitPlan.title.value")}
            </span>
          </span>
        );
      }
      return leOrUnitName;
    };
    return _.get(feedList, "length", 0) > 0 ? (
      _.map(feedList, post => {
        const node = _.get(post, "node", {});
        const leAndStudentCount = getGoalsAndStudentCount(node);

        const title =
          _.size(_.get(node, "title")) > 0 && _.get(node, "title") !== "null"
            ? _.get(node, "title")
            : t("common:untitled");
        return (
          <div
            onClick={() => onItemClicked(post)}
            className={classes.singlePostItemContainer}
            key={_.get(post, "id")}
          >
            <div className={classes.postAvatar}>
              <ResourceImagePlaceHolder
                id={post?.id}
                imageUrl={
                  getPostImage(node) ||
                  PLACEHOLDER_RECT_IMAGE[
                    _.get(node, "id") % PLACEHOLDER_RECT_IMAGE_COUNT
                  ]
                }
                imageThumbSize={56}
                containerStyle={styles.newImagePlaceholder}
              />
            </div>
            <div className={classes.postDetailsContainer}>
              <div className={classes.postTitle}>{title}</div>
              <div className={classes.resourceCentralIdea}>
                {getLeOrUnitName(post)}
                {leAndStudentCount.goalsCount > 0 ? (
                  <>
                    {getLeOrUnitName(post) ? (
                      <div className={classes.postListDot}></div>
                    ) : null}

                    {leAndStudentCount.goalsCount === 0 ? null : (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <TagOutlined size={"xxxx-small"} variant={"subtle"} />
                        {leAndStudentCount.goalsCount +
                          " " +
                          t(
                            leAndStudentCount.goalsCount === 1
                              ? "journal:goal"
                              : "journal:goals"
                          )}
                      </span>
                    )}
                  </>
                ) : null}
              </div>
            </div>
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              icon={<ChevronRightOutlined size={"x-small"} />}
              type={"plain"}
              size={"x-small"}
            />
          </div>
        );
      })
    ) : (
      <div className={classes.noDataContainer}>
        <EmptyState
          illustration={EvidenceIllustration}
          size="small"
          title={t("common:empty_instructions_title_text")}
          subtitleStyle={emptyStateSubtitleStyle}
        />
      </div>
    );
  };

  const { showAlert } = props;
  return (
    <div className={classes.listContainer}>
      <div
        className={classes.listContent}
        style={{
          ...(showAlert
            ? styles.listStyleWithAlert
            : styles.listStyleWithoutAlert),
        }}
      >
        {renderPosts()}
      </div>
    </div>
  );
};

export default I18nHOC({ resource: ["common", "journal"] })(PostList);
